/**
 * SearchDescription component.
 */
import Classification from '@components/elements/description/descriptionClassification';
import Container from '@components/atoms/container/searchDescriptionContainer';
import Date from '@components/elements/description/descriptionDate';
import DescriptionContext from '@shared/components/utils/context/descriptionContext';
import Event from '@shared/classes/event';
import Facts from '@components/elements/description/descriptionFacts';
import Focus from '@shared/types/enums/focus';
import FocusUtils from '@shared/utils/focus';
import Header from '@components/atoms/container/searchHeaderContainer';
import Info from '@components/atoms/container/descriptionInfoContainer';
import Synopsis from '@components/atoms/text/descriptionSynopsis';
import Tip from '@components/elements/description/descriptionTip';
import Title from '@components/atoms/text/descriptionTitle';
import useMetadata from '@shared/components/utils/hooks/useMetadata';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { selectMainAsset } from '@shared/store/slices/main.slice';
import { setDescription } from '@shared/store/slices/app.slice';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';

/**
 * SearchDescription component.
 *
 * @param {Event} asset - The asset to display the description for.
 * @returns {React.ReactNode} The SearchDescription component.
 */
export default function SearchDescription() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);
  const asset = useAppSelector(selectMainAsset);
  const { synopsis, title } = (asset as Event) || {};

  // Metadata
  const metadata = useMetadata({ asset: asset as Event });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  const { display } = useMemo(() => {
    const display =
      FocusUtils.evaluateFocus(Focus.RAILS_LIST) ||
      focus === Focus.MODAL_INPUT ||
      focus === Focus.FLOATING_MENU;
    return { display };
  }, [focus]);

  // ------------------------------------------------------------
  // Context
  // ------------------------------------------------------------

  const descriptionContext = {
    asset: asset as Event | null,
    display,
    metadata,
  };
  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setDescription(display));
  }, [display]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <DescriptionContext.Provider value={descriptionContext}>
      <Container display={display}>
        <Header>
          <Title limitWidth={true}>{title}</Title>
        </Header>
        <Info>
          <Date />
          <Facts />
          <Classification />
        </Info>
        <Synopsis limitWidth={true} maxLines={2}>
          {synopsis}
        </Synopsis>
        <Tip />
      </Container>
    </DescriptionContext.Provider>
  );
}
